import { getUIAssetPath } from 'components/atoms/UIAsset/UIAsset';
import i18n from 'helper/i18n';
import * as React from 'react';
import Knob from '../../atoms/Knob/Knob';

export type TimerProps = {
	id: string | number; // used to reset timer states if id changes
	seconds: number;
};

export type TimerStateTypes = {
	totalSeconds: number;
	remainingSeconds: number;
	remainingSecondsBeforeStart: number;
	timer: any;
	timerBeforeStart: any;
	isTimerRunning: boolean;
	widthPercentage: number;
	icon: string;
	isBeforeTimerStart: boolean;
};

function playAudio(audio) {
	return new Promise((res) => {
		audio.play();
		audio.onended = res;
	});
}

const TIMER_UPDATE_STEP_MS = 1000;
const SECONDS_FOR_PLAYING_SOUND = 3;
const AUDIO_START = getUIAssetPath('audio/countdown-start.mp3');
const AUDIO_DONE = getUIAssetPath('audio/countdown-done.mp3');
const AUDIO_ELEMENT_START = new Audio(AUDIO_START);
const AUDIO_ELEMENT_DONE = new Audio(AUDIO_DONE);

export class Timer extends React.Component<TimerProps, TimerStateTypes> {
	constructor(props: TimerProps) {
		super(props);
		this.state = {
			totalSeconds: this.props.seconds,
			remainingSeconds: this.props.seconds,
			remainingSecondsBeforeStart: SECONDS_FOR_PLAYING_SOUND,
			timer: null,
			timerBeforeStart: null,
			isTimerRunning: false,
			widthPercentage: 0,
			icon: 'start',
			isBeforeTimerStart: false,
		};
	}

	public componentWillUnmount() {
		clearInterval(this.state.timer);
		clearInterval(this.state.timerBeforeStart);
	}

	public componentDidUpdate(prevProps) {
		if (prevProps.id !== this.props.id || prevProps.seconds !== this.props.seconds) {
			this.setState({ totalSeconds: this.props.seconds });
			this.setState({ remainingSeconds: this.props.seconds });
			this.setState({ icon: 'start' });
			this.setState({ widthPercentage: 0 });
		}
	}

	public render() {
		const { widthPercentage, isBeforeTimerStart, remainingSecondsBeforeStart, icon } = this.state;
		return (
			<div className="m-timer" style={{ backgroundColor: isBeforeTimerStart ? '#ff6600' : '#00838f' }}>
				<audio>
					<source src={AUDIO_START} type="audio/mpeg" />
				</audio>
				<audio>
					<source src={AUDIO_DONE} type="audio/mpeg" />
				</audio>
				<div className="m-timer__time" style={{ width: `${widthPercentage}%` }} />
				<div className={`m-timer__line ${isBeforeTimerStart && 'm-timer__line--before-timer-start'}`}>
					{!isBeforeTimerStart ? (
						<>
							<span className="m-timer__text">
								{icon === 'restart'
									? i18n('react.impulsCoach.components.cardDetail.workout.timer.doneText')
									: this.formatRemainingTime()}
							</span>
							<span className="m-timer__icon">
								<Knob modifier="filled-white" size="l" icon={icon} onClick={this.handleTimerButton} />
							</span>
						</>
					) : (
						<span className="m-timer__text">{remainingSecondsBeforeStart}</span>
					)}
				</div>
			</div>
		);
	}

	private formatRemainingTime = () => {
		const { remainingSeconds } = this.state;
		const mins = Math.floor((remainingSeconds % 3600) / 60);
		const secs = Math.floor(remainingSeconds % 60);

		return `${mins < 10 ? '0' : ''}${mins}:${secs < 10 ? '0' : ''}${secs}`;
	};

	private renderTimer = () => {
		const { totalSeconds, remainingSeconds } = this.state;
		const widthPercentage = (100 / totalSeconds) * (totalSeconds - remainingSeconds);
		if (widthPercentage <= 100) {
			this.setState({ widthPercentage });
		}
	};

	private endTimer = () => {
		this.setState({
			isTimerRunning: false,
			remainingSeconds: this.state.totalSeconds,
			icon: 'restart',
			remainingSecondsBeforeStart: SECONDS_FOR_PLAYING_SOUND,
		});
		clearInterval(this.state.timer);
	};

	private startTimer = () => {
		this.setState({
			isTimerRunning: true,
			timer: setInterval(() => {
				const { remainingSeconds } = this.state;
				this.setState(({ remainingSeconds }) => ({
					remainingSeconds: remainingSeconds - 1,
				}));
				if (remainingSeconds >= 0) {
					this.renderTimer();
				}
				if (remainingSeconds === SECONDS_FOR_PLAYING_SOUND + 1) {
					playAudio(AUDIO_ELEMENT_DONE);
				}
				if (remainingSeconds <= 0) {
					this.endTimer();
				}
			}, TIMER_UPDATE_STEP_MS),
		});
	};

	private startTimerBeforeStart = () => {
		this.setState({
			timerBeforeStart: setInterval(() => {
				const { remainingSecondsBeforeStart, timerBeforeStart } = this.state;
				this.setState(({ remainingSecondsBeforeStart }) => ({
					remainingSecondsBeforeStart: remainingSecondsBeforeStart - 1,
				}));
				if (remainingSecondsBeforeStart <= 1) {
					clearInterval(timerBeforeStart);
					return;
				}
			}, TIMER_UPDATE_STEP_MS),
		});
	};

	private pauseTimer = () => {
		this.setState({ isTimerRunning: false });
		clearInterval(this.state.timer);
	};

	private handleTimerButton = () => {
		const { isTimerRunning, remainingSecondsBeforeStart } = this.state;
		if (!isTimerRunning) {
			if (remainingSecondsBeforeStart > 0) {
				this.setState({ widthPercentage: 0, isBeforeTimerStart: true, icon: 'pause' });
				this.startTimerBeforeStart();
				playAudio(AUDIO_ELEMENT_START)
					.then(() => this.setState({ isBeforeTimerStart: false }))
					.then(this.startTimer);
				return;
			}
			this.setState({ icon: 'pause' });
			this.startTimer();
		} else {
			this.pauseTimer();
			this.setState({ icon: 'start' });
		}
	};
}

export default Timer;
